import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ManagementList from '../components/ManagementList';
import { Button, Modal } from 'react-bootstrap';
import {
  deactivateUser,
  fetchCaseManagerAssignableCustomers,
  fetchCaseManagerAssignedCustomers,
  fetchUserList,
  reactivateUser,
} from '../services/api';
import PaymentMethodsModal from '../components/PaymentMethodsModal'; // Import the new modal
import AssignPatientsModal from '../components/AssignPatientsModal';
import ManageRolesPermissions from '../components/ManageRolesPernissions/ManageRolesPermissions';
import { getCurrentUser } from '../services/authService';
import { LIMIT, USER_ROLES } from '../lib/utils/constant';
import { usePermissions } from '../Hooks/usePermission';
import { PERMISSIONS } from '../lib/utils/permissions';
import PermissionMessage from '../components/ErrorMessage/PermissionMessage';

const columns = [
  { header: 'FIRST NAME', accessor: 'firstName', sortable: true },
  { header: 'LAST NAME', accessor: 'lastName', sortable: true },
  { header: 'EMAIL', accessor: 'email', sortable: true },
  { header: 'ROLE', accessor: 'role', sortable: true },
  { header: 'ENTITY/CLIENT NAME', accessor: 'entityClientName', sortable: true },
  { header: 'REASON', accessor: 'deactivationReason', sortable: true, visibleOnTabs: ['deactivated'] },
  { header: 'LAST LOGIN', accessor: 'lastLoggedIn', sortable: true, sortKey: 'raw_lastLoggedIn' },
  { header: 'ACTION', accessor: 'action', align: 'end' },
];

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
   // --- Auth Check ---
  const currentUser = getCurrentUser();

  // --- Payment Modal State ---
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [selectedEntityName, setSelectedEntityName] = useState('');
  const [showAssignPatientsModal, setShowAssignPatientsModal] = useState(false);
  const [selectedAssignUser, setSelectedAssignUser] = useState(null);
  const [assignPatientsLoading, setAssignPatientsLoading] = useState(false);
  const [assignPatientsErrorMessage, setAssignPatientsErrorMessage] = useState('');
  const [assignPatientsInitData, setAssignPatientsInitData] = useState({
    unassigned: [],
    assigned: [],
  });
  const [showManageRolesModal, setShowManageRolesModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // --- API State ---
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(LIMIT[0].value)
  const [statusFilter, setStatusFilter] = useState('A'); // Default to 'A' (Active)
  const [counts, setCounts] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // Local search state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { can } = usePermissions();
  const userRoleCode = JSON.parse(localStorage.getItem('user'))?.role?.code;
  const canViewUser = can(PERMISSIONS.USER_READ, userRoleCode);
  const canCreateUser = can(PERMISSIONS.USER_CREATE,userRoleCode);
  const canUpdateUser = can(PERMISSIONS.USER_UPDATE,userRoleCode);
  const canDeactivateUser = can(PERMISSIONS.USER_DEACTIVATE,userRoleCode);
  const canViewPayment = can(PERMISSIONS.PAYMENT_READ,userRoleCode);
  const canManageRoles = can(PERMISSIONS.RBAC_MANAGE,userRoleCode);
  const canViewRoles = can(PERMISSIONS.RBAC_READ,userRoleCode);
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const handleLimitChange = (e) => {
    setItemsPerPage(e.target.value)
  }

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      // --- 1. REMOVED SEARCH FROM PAYLOAD ---
      // We only fetch based on date, page, and status now
      const payload = {
        start: null,
        end: null,
        page: page,
        limit: itemsPerPage,
        status: statusFilter
        // search: searchTerm  <-- Removed this line
      };

      const response = await fetchUserList(payload);

      if (response.data && response.data.success && response.data.data) {
        const apiData = response.data.data;

        const mappedUsers = (apiData.items || []).map(u => ({
          id: u.id,
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.emailAddress,
          role: u.role?.name || 'N/A',
          entityClientName: u.entityMaster?.name || 'N/A',
          lastLoggedIn: u.lastLoggedIn ? new Date(u.lastLoggedIn).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            //second: '2-digit',
            hour12: true
          })
            : '-',
          raw_lastLoggedIn: u.lastLoggedIn || null,
          deactivationReason: u.deactivationReason || u.deactivation_reason || '-',
          status: u.status,
          originalData: u
        }));

        setUsers(mappedUsers);
        setTotalPages(apiData.pagination?.pages || 1);
        setCounts(apiData.counts);
      } else {
        setUsers([]);
        setTotalPages(1);
        setCounts(null);
      }

    } catch (error) {
      console.error("Error loading users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, page, statusFilter]);

  // --- 2. REMOVED searchTerm FROM DEPENDENCY ARRAY ---
  // The API won't be called when the user types
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleTabChange = (tabKey) => {
    let apiStatus = 'A';
    if (tabKey === 'active') apiStatus = 'A';
    if (tabKey === 'pending') apiStatus = 'R';
    if (tabKey === 'deactivated') apiStatus = 'D';
    if (tabKey === 'all') apiStatus = 'ALL';

    setStatusFilter(apiStatus);
    setPage(1);
    setSortConfig({ key: null, direction: 'asc' });
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    // No need to reset page for client-side filtering of current page data
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // --- 3. CLIENT SIDE FILTERING ---
  // Filter the currently fetched 'users' array based on the search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return (
      (user.firstName && user.firstName.toLowerCase().includes(lowerTerm)) ||
      (user.lastName && user.lastName.toLowerCase().includes(lowerTerm)) ||
      (user.email && user.email.toLowerCase().includes(lowerTerm)) ||
      (user.role && user.role.toLowerCase().includes(lowerTerm)) ||
      (user.entityClientName && user.entityClientName.toLowerCase().includes(lowerTerm))
    );
  });

  const sortedUsers = useMemo(() => {
    const items = [...filteredUsers];
    if (!sortConfig.key) return items;

    items.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'raw_lastLoggedIn') {
        const aTime = aValue ? new Date(aValue).getTime() : null;
        const bTime = bValue ? new Date(bValue).getTime() : null;
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
      }

      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return items;
  }, [filteredUsers, sortConfig]);

  // --- Logic to determine if the Credit Card icon should appear for a specific row ---
  const shouldShowManageCards = (item) => {
      const rowRoleCode = item.originalData?.role?.code;
      const entity = item.originalData?.entityMaster;

      // 2. User Row must be an Admin/System Admin type role (ADMIN, SYS_ADMIN, SUPER_ADMIN)
      // Note: Adjust specific codes if needed based on your DB values
      const isAdminRow = ['ADMIN', 'SYS_ADMIN', 'SUPER_ADMIN'].includes(rowRoleCode);
      
      // 3. Entity configuration chargeToCustomer must be FALSE
      const isEntityPaying = entity && entity.charge_to_customer === false;

      return isAdminRow && isEntityPaying;
  };

  // --- Handle Manage Cards Click ---
  const handleManageCards = (item) => {
     if (shouldShowManageCards(item)) {
         const entity = item.originalData.entityMaster;
         setSelectedEntityId(entity.id);
         setSelectedEntityName(entity.name);
         setShowPaymentModal(true);
     } else {
         console.warn("Manage cards not allowed for this user configuration.");
     }
  };

  // Show Assign Patients only for Case Manager / CRE users with ASSIGNED access mode.
  const shouldShowAssignPatients = (item) => {
    const roleCode = item.originalData?.role?.code;
    const accessMode =
      item.originalData?.caseManagerAccessMode ||
      item.originalData?.entityMaster?.caseManagerAccessMode;
    return (
      roleCode === USER_ROLES.CASE_MANAGER && accessMode === 'ASSIGNED'
    );
  };

  const handleAssignPatients = (item) => {
    if (!shouldShowAssignPatients(item)) return;
    setSelectedAssignUser(item);
    setShowAssignPatientsModal(true);

    // Fetch assignable + already-assigned patients when modal opens.
    const load = async () => {
      setAssignPatientsLoading(true);
      setAssignPatientsErrorMessage('');
      setAssignPatientsInitData({ unassigned: [], assigned: [] });

      try {
        const payload = { limit: 100, page: 1, search: '' };

        const normalizeList = (v) => {
          if (Array.isArray(v)) return v;
          if (Array.isArray(v?.items)) return v.items;
          return [];
        };

        const [assignableRes, assignedRes] = await Promise.all([
          fetchCaseManagerAssignableCustomers(item.id, payload),
          fetchCaseManagerAssignedCustomers(item.id, payload),
        ]);

        const assignableData =
          assignableRes?.data?.data ?? assignableRes?.data ?? {};
        const assignedData = assignedRes?.data?.data ?? assignedRes?.data ?? {};

        const unassignedList = normalizeList(
          assignableData?.unassignedCustomers ??
            assignableData?.unassigned ??
            assignableData?.items ??
            assignableData,
        );
        const assignedList = normalizeList(
          assignedData?.assignedCustomers ??
            assignedData?.assigned ??
            assignedData?.items ??
            assignedData,
        );

        setAssignPatientsInitData({
          unassigned: unassignedList,
          assigned: assignedList,
        });
      } catch (e) {
        console.error('Failed to load assignable patients:', e);
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load assignable patients.';
        setAssignPatientsErrorMessage(msg);
      } finally {
        setAssignPatientsLoading(false);
      }
    };

    load();
  };

  const handleDeactivate = async (id, reason) => {
    try {
      const response = await deactivateUser(id, reason);
      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || 'Failed to deactivate user.');
      }
      setSuccessMessage(response?.data?.message || 'User deactivated successfully.');
      setShowSuccessModal(true);
      await loadUsers();
      return true;
    } catch (err) {
      console.error("Error deactivating user:", err);
      const apiMessage = err?.response?.data?.message || err?.message || 'Failed to deactivate user.';
      setErrorMessage(apiMessage);
      setShowErrorModal(true);
      return false;
    }
  };

  const handleReactivate = async (id) => {
    try {
      const response = await reactivateUser(id);
      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || 'Failed to reactivate user.');
      }
      setSuccessMessage(response?.data?.message || 'User reactivated successfully.');
      setShowSuccessModal(true);
      await loadUsers();
    } catch (err) {
      console.error("Error reactivating user:", err);
      const apiMessage = err?.response?.data?.message || err?.message || 'Failed to reactivate user.';
      setErrorMessage(apiMessage);
      setShowErrorModal(true);
    }
  };
  const isAdminDeactivateDisabled = (user) => user?.id === currentUser?.id;
  return (
    <>
      {canViewUser ? <ManagementList
        title="Admin Users"
        data={sortedUsers}
        columns={columns}
        addNewLink="/users/new"
        editLinkBase="/users/edit"
        //onDelete={handleDelete}
        onManageCards={handleManageCards}
        shouldShowManageCards={shouldShowManageCards}
        onAssignPatients={handleAssignPatients}
        shouldShowAssignPatients={shouldShowAssignPatients}
        isServerSide={true}
        isLoading={loading}
        serverPage={page}
        serverTotalPages={totalPages}
        serverCounts={counts}
        onPageChange={(newPage) => setPage(newPage)}
        onTabChange={handleTabChange}
        deactivationType="user"
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
        onSearch={handleSearch}
        onSort={handleSort}
        sortConfig={sortConfig}
        limit={itemsPerPage}
        handleLimitChange={handleLimitChange}
        canCreate={canCreateUser}
        canUpdate={canUpdateUser}
        canDeactivate={canDeactivateUser}
        canReactivate={!canCreateUser}
        canViewPayment={canViewPayment}
        onManageRolesPermissions={() => setShowManageRolesModal(true)}
        canViewRoles={canViewRoles}
        isAdminDeactivateDisabled={isAdminDeactivateDisabled}
      /> : <PermissionMessage  />}

      {/* Payment Modal */}
      {
        showPaymentModal && (
          <PaymentMethodsModal
            show={showPaymentModal}
            onHide={() => setShowPaymentModal(false)}
            entityId={selectedEntityId}
            entityName={selectedEntityName}
          />
        )
      }

      {showAssignPatientsModal && selectedAssignUser && (
        <AssignPatientsModal
          show={showAssignPatientsModal}
          onHide={() => {
            setShowAssignPatientsModal(false);
            setSelectedAssignUser(null);
            setAssignPatientsLoading(false);
            setAssignPatientsErrorMessage('');
            setAssignPatientsInitData({ unassigned: [], assigned: [] });
          }}
          user={selectedAssignUser}
          initialUnassigned={assignPatientsInitData.unassigned}
          initialAssigned={assignPatientsInitData.assigned}
          initialIsLoading={assignPatientsLoading}
          initialLoadErrorMessage={assignPatientsErrorMessage}
        />
      )}

      {showManageRolesModal && (
        <ManageRolesPermissions
          show={showManageRolesModal}
          onHide={() => setShowManageRolesModal(false)}
          canManageRoles={canManageRoles}
        />
      )}

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton><Modal.Title className="text-success">Success</Modal.Title></Modal.Header>
        <Modal.Body>{successMessage}</Modal.Body>
        <Modal.Footer><Button variant="success" onClick={() => setShowSuccessModal(false)}>OK</Button></Modal.Footer>
      </Modal>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton><Modal.Title className="text-danger">Error</Modal.Title></Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowErrorModal(false)}>Close</Button></Modal.Footer>
      </Modal>
    </>
  );
}

export default UserManagement;